import { storeToRefs } from 'pinia';
import searchJobsRules from '@/constants/searchJobsRules';
import { useFilterStore, useSavedSearchStore } from '@/stores/filters';
import { useOverlayScrollHelper } from '@/composables/useOverlayScrollHelper';
const removeReactivityFromSpecificValue = (value) => JSON.parse(JSON.stringify(value));

export function useSavedSearchUtils() {
  const { trackEvent, eventNames } = useAcvAnalytics();
  const route = useRoute();
  const savedSearchStore = useSavedSearchStore();

  const { getSavedSearchesForCarrier, resetFormData, setActiveSavedSearch, saveSearch } =
    savedSearchStore;

  const { allowBgToScroll, preventBgFromScrolling } = useOverlayScrollHelper();

  const {
    isSavedSearchSideBarOpen,
    isCreateFormOpen,
    isQuickSave,
    savedSearches,
    savedSearchEditID,
    activeSavedSearch,
  } = storeToRefs(savedSearchStore);

  const useFilters = useFilterStore();
  const filtersOptions = storeToRefs(useFilters);

  const savedSearchToggle = () => {
    if (!isSavedSearchSideBarOpen.value) {
      getSavedSearchesForCarrier();
    } else {
      // if closing turn on bg scrolling
      allowBgToScroll();
    }

    isSavedSearchSideBarOpen.value = !isSavedSearchSideBarOpen.value;
  };

  const createFormToggle = () => {
    // if not open and sidebar not open, first open sidebar
    if (!isCreateFormOpen.value && !isSavedSearchSideBarOpen.value) {
      savedSearchToggle();
    }

    // then open create form
    isCreateFormOpen.value = !isCreateFormOpen.value;

    // if closing, reset the form data, get carrier saved searches if needed
    if (!isCreateFormOpen.value) {
      resetFormData();
    }
  };

  const onSaveSearch = async () => {
    const savedSearchID = await saveSearch();
    await setActiveSavedSearch(savedSearchID);
    createFormToggle();
    toggleItem({ value: true, savedSearchID });
  };

  const convertActiveFiltersToSavedSearchFormData = () => {
    const { location, sort, jobTypes } = filtersOptions;
    // strip data of reactive properties so form doesn't impact current filters
    const locationFilters = removeReactivityFromSpecificValue(location.value);
    const sortFilters = removeReactivityFromSpecificValue(sort.value);
    const jobTypeFilters = removeReactivityFromSpecificValue(jobTypes.value);

    savedSearchStore.location = locationFilters;
    savedSearchStore.sort = sortFilters;
    savedSearchStore.jobTypes = jobTypeFilters;
  };

  const openQuickSaveForm = () => {
    isQuickSave.value = true;
    // this is a mobile-only function, so we can turn off scrolling
    preventBgFromScrolling();

    createFormToggle();
  };

  const noSavedSearches = computed(() => savedSearches.value.length === 0);

  const computedOrderOptions = computed(() => {
    return searchJobsRules.DEFAULT.SORT_BY.options.map((item) => ({
      label: item.text,
      value: item.value,
    }));
  });

  const toggleItem = async (params) => {
    if (params.value === true) {
      const savedSearchLocation = activeSavedSearch.value.searchCriteria.location;
      const parsedSort = removeReactivityFromSpecificValue(
        activeSavedSearch.value.searchCriteria.sort,
      );

      const [value, order] = parsedSort.split(',');
      const { location, sort, jobTypes } = filtersOptions;

      location.value = removeReactivityFromSpecificValue(savedSearchLocation);
      const sortOption = searchJobsRules.DEFAULT.SORT_BY.options.find(
        (item) => item.value === value,
      );

      sort.value = {
        order,
        value,
        text: sortOption?.text,
      };

      jobTypes.value = activeSavedSearch.value.searchCriteria?.jobTypes || [];

      trackEvent(eventNames.SAVED_SEARCH_APPLIED, {
        saved_search_id: params.savedSearchID,
        sort: sort.value,
      });
    } else {
      setActiveSavedSearch(null);
      useFilters.$reset();
      trackEvent(eventNames.SAVED_SEARCH_REMOVED, {
        saved_search_id: params.savedSearchID,
      });
    }
  };

  const computedFormTitle = computed(() =>
    savedSearchEditID.value ? 'Edit Saved Search' : 'Create a Saved Search',
  );

  const computedShowSaveItButton = computed(
    () =>
      !activeSavedSearch.value &&
      !route.path.includes('manage-jobs') &&
      filtersOptions.appliedFilters.value.length > 0,
  );

  return {
    savedSearchToggle,
    toggleItem,
    createFormToggle,
    convertActiveFiltersToSavedSearchFormData,
    openQuickSaveForm,
    onSaveSearch,
    computedOrderOptions,
    noSavedSearches,
    computedFormTitle,
    computedShowSaveItButton,
  };
}
